"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Search, Wrench, GitCommitVertical, Activity, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const services = [{
  id: "emergency",
  title: "Emergency Plumbing",
  description: "Rapid dispatch for burst pipes, sudden floods, and severe water emergencies across Jonesboro 24 hours a day.",
  badge: "24/7 Rapid Response",
  href: "/emergency-plumbing/",
  iconKey: "emergency"
}, {
  id: "drain",
  title: "Drain Cleaning",
  description: "Clearing tough clogs, standing water, and stubborn grease from sinks, tubs, and main lines using professional equipment.",
  badge: "Same-Day Service",
  href: "/drain-cleaning/",
  iconKey: "drain"
}, {
  id: "water-heater",
  title: "Water Heater Repair",
  description: "Fixing cold showers, tank leaks, thermostat failures, and heating element faults on standard and tankless units.",
  badge: "All Major Brands",
  href: "/water-heater-repair/",
  iconKey: "heater"
}, {
  id: "leak-detection",
  title: "Leak Detection",
  description: "Non-invasive diagnostic tools to pinpoint hidden slab leaks, wall drips, and unexplained water pressure drops.",
  badge: "Pinpoint Accuracy",
  href: "/leak-detection-repair/",
  iconKey: "leak"
}, {
  id: "toilet",
  title: "Toilet Repair",
  description: "Stopping continuous running, replacing broken flush valves, rebuilding seals, and resolving recurrent toilet backups.",
  badge: "Fast Turnaround",
  href: "/toilet-repair/",
  iconKey: "toilet"
}, {
  id: "sewer",
  title: "Sewer Line Service",
  description: "Comprehensive video camera inspections, trenchless pipe solutions, root removal, and main line sewer repairs.",
  badge: "Camera Inspection",
  href: "/sewer-line-repair/",
  iconKey: "sewer"
}, {
  id: "pipe-repair",
  title: "Pipe Repair & Repiping",
  description: "Durable fixes for corroded copper, cracked PVC, leaking joints, and complete home repiping services.",
  badge: "Durable Solutions",
  href: "/pipe-repair/",
  iconKey: "pipe"
}, {
  id: "faucet",
  title: "Faucet & Sink Repair",
  description: "Repairing dripping taps, damaged cartridges, faulty sprayer heads, and installing modern kitchen and bath fixtures.",
  badge: "Fixture Specialists",
  href: "/faucet-sink-repair/",
  iconKey: "faucet"
}];
function ServiceIcon({
  iconKey
}: {
  iconKey: string;
}) {
  const iconClass = "size-6 text-primary";
  switch (iconKey) {
    case "emergency":
      return <AlertTriangle className={iconClass} />;
    case "drain":
      return <Droplets className={iconClass} />;
    case "heater":
      return <Flame className={iconClass} />;
    case "leak":
      return <Search className={iconClass} />;
    case "toilet":
      return <Activity className={iconClass} />;
    case "sewer":
      return <GitCommitVertical className={iconClass} />;
    case "pipe":
      return <Wrench className={iconClass} />;
    case "faucet":
    default:
      return <Droplets className={iconClass} />;
  }
}
export default function ServicesGrid() {
  const prefersReduced = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReduced ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReduced ? 1 : 0,
      y: prefersReduced ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold">
            Comprehensive Plumbing Services
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold font-default text-foreground tracking-tight">
            Fast, Reliable Plumbing Solutions in Jonesboro, GA
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From critical midnight leaks to routine fixture installations, our licensed local technicians solve plumbing problems with speed, upfront pricing, and lasting craftsmanship.
          </p>
        </div>

        {/* 8-Grid Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => <motion.div key={service.id} data-index={index} variants={itemVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-lg bg-secondary flex items-center justify-center">
                      <ServiceIcon iconKey={service.iconKey} />
                    </div>
                    <Badge variant="outline" className="text-[11px] font-medium border-border">
                      {service.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold font-default text-foreground pt-1">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm leading-normal">
                    {service.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-0" />

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" className="w-full justify-between group text-foreground font-medium hover:bg-primary hover:text-primary-foreground transition-[color,background-color] duration-200">
                    <Link to={service.href}>
                      <span>Explore Service</span>
                      <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Urgent Callout Footer */}
        <div className="mt-12 sm:mt-16 rounded-2xl bg-secondary/50 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1">
            <h3 className="text-lg sm:text-xl font-bold font-default text-foreground">
              Facing an active plumbing emergency right now?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our emergency crew is on standby 24/7 across Jonesboro and surrounding Clayton County areas.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild size="lg" className="shadow-sm">
              <a href="tel:4708479076" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contact/">Contact Form</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}